import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { FontSize, TextStyle } from '@tiptap/extension-text-style';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { normalizeQuoteFooterHtml } from '../../lib/unsavedFormState';
import styles from './RichTextEditor.module.css';

const DEFAULT_FONT_SIZE = '14px';

const FONT_SIZE_OPTIONS = [
  { label: 'Small', value: '12px' },
  { label: 'Default', value: DEFAULT_FONT_SIZE },
  { label: 'Large', value: '18px' },
  { label: 'Title', value: '22px' },
] as const;

type Props = {
  label?: string;
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  hint?: string;
};

export function RichTextEditor({ label, value, onChange, disabled, hint }: Props) {
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // TipTap rewrites HTML on load; do not notify the parent until the user interacts.
  const allowEmitRef = useRef(false);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
      }),
      TextStyle,
      FontSize,
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed, transaction }) => {
      if (!allowEmitRef.current) return;
      if (!transaction.docChanged) return;
      const html = ed.getHTML();
      if (normalizeQuoteFooterHtml(html) === normalizeQuoteFooterHtml(valueRef.current)) {
        return;
      }
      onChangeRef.current(html);
    },
    editorProps: {
      attributes: {
        class: styles.prose ?? '',
      },
    },
  });

  const toolbar = useEditorState({
    editor,
    selector: ({ editor: ed }) => {
      if (!ed) {
        return {
          fontSize: '',
          bold: false,
          italic: false,
          heading: false,
          bulletList: false,
          orderedList: false,
        };
      }
      const attrs = ed.getAttributes('textStyle') as { fontSize?: string | null };
      return {
        fontSize: attrs.fontSize || '',
        bold: ed.isActive('bold'),
        italic: ed.isActive('italic'),
        heading: ed.isActive('heading', { level: 3 }),
        bulletList: ed.isActive('bulletList'),
        orderedList: ed.isActive('orderedList'),
      };
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const enableEmit = () => {
      allowEmitRef.current = true;
    };
    editor.on('focus', enableEmit);
    return () => {
      editor.off('focus', enableEmit);
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (normalizeQuoteFooterHtml(value) === normalizeQuoteFooterHtml(current)) {
      return;
    }
    // Programmatic sync — keep parent notifications suppressed.
    const prev = allowEmitRef.current;
    allowEmitRef.current = false;
    editor.commands.setContent(value || '', { emitUpdate: false });
    allowEmitRef.current = prev;
  }, [editor, value]);

  function markUserEdit() {
    allowEmitRef.current = true;
  }

  if (!editor || !toolbar) return null;

  return (
    <div className={clsx(styles.wrap, disabled && styles.disabled)}>
      {label ? <span className={styles.label}>{label}</span> : null}
      {hint ? <p className={styles.hint}>{hint}</p> : null}
      <div className={styles.toolbar} role="toolbar" aria-label="Formatting">
        <button
          type="button"
          className={clsx(styles.toolBtn, toolbar.bold && styles.toolActive)}
          disabled={disabled}
          onMouseDown={markUserEdit}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          className={clsx(styles.toolBtn, styles.italic, toolbar.italic && styles.toolActive)}
          disabled={disabled}
          onMouseDown={markUserEdit}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          I
        </button>
        <span className={styles.sep} />
        <label className={styles.sizeWrap}>
          <span className={styles.sizeLabel}>Size</span>
          <select
            className={styles.sizeSelect}
            disabled={disabled}
            value={toolbar.fontSize || DEFAULT_FONT_SIZE}
            onMouseDown={markUserEdit}
            onChange={(e) => {
              markUserEdit();
              const next = e.target.value || DEFAULT_FONT_SIZE;
              editor.chain().focus().setFontSize(next).run();
            }}
            title="Text Size"
            aria-label="Text Size"
          >
            {FONT_SIZE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <span className={styles.sep} />
        <button
          type="button"
          className={clsx(styles.toolBtn, toolbar.heading && styles.toolActive)}
          disabled={disabled}
          onMouseDown={markUserEdit}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="Heading"
        >
          H
        </button>
        <button
          type="button"
          className={clsx(styles.toolBtn, toolbar.bulletList && styles.toolActive)}
          disabled={disabled}
          onMouseDown={markUserEdit}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          className={clsx(styles.toolBtn, toolbar.orderedList && styles.toolActive)}
          disabled={disabled}
          onMouseDown={markUserEdit}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered List"
        >
          1. List
        </button>
      </div>
      <EditorContent editor={editor} className={styles.surface} />
    </div>
  );
}
